import React, { useState, useEffect } from 'react';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Label } from '@vehix/portal/components/ui/label';
import { Switch } from '@vehix/portal/components/ui/switch';
import { Textarea } from '@vehix/portal/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@vehix/portal/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@vehix/portal/components/ui/select';
import { Loader2, Zap, Globe, Building2 } from 'lucide-react';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import { useTenants } from '@vehix/portal/hooks/useTenants';
import { useCreateAutomationRule, useUpdateAutomationRule } from '@vehix/portal/hooks/useScheduling';
import {
  AutomationRule,
  RuleCreateRequest,
  RuleUpdateRequest,
  TriggerConditions,
  ActionConfig
} from '@vehix/portal/services/api/scheduling';
import { cn } from '@vehix/portal/lib/utils';

interface AutomationRuleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: AutomationRule | null;
  defaultTenantId?: string;
}

const OEM_OPTIONS = [
  { value: '__any__', label: 'Any OEM' },
  { value: 'ford', label: 'Ford' },
  { value: 'stellantis', label: 'Stellantis' },
  { value: 'hyundai', label: 'Hyundai' }
];

const SAMPLING_OPTIONS = [
  { value: 'all', label: 'All VINs' },
  { value: 'random', label: 'Random sample' },
  { value: 'first', label: 'First N' },
  { value: 'last', label: 'Last N' }
];

export const AutomationRuleFormDialog: React.FC<AutomationRuleFormDialogProps> = ({
  open,
  onOpenChange,
  rule,
  defaultTenantId
}) => {
  const { toast } = useToast();
  const isEditing = !!rule;

  // Form state
  const [name, setName] = useState(rule?.name || '');
  const [description, setDescription] = useState(rule?.description || '');
  const [isGlobal, setIsGlobal] = useState(!rule?.tenant_id);
  const [tenantId, setTenantId] = useState(rule?.tenant_id || defaultTenantId || '');
  const [priority, setPriority] = useState(rule?.priority || 100);
  const [isEnabled, setIsEnabled] = useState(rule?.is_enabled ?? true);

  // Trigger conditions
  const [triggerTaskName, setTriggerTaskName] = useState(rule?.trigger_conditions?.task_name || '');
  const [triggerOem, setTriggerOem] = useState(rule?.trigger_conditions?.oem || '__any__');
  const [minRecords, setMinRecords] = useState(rule?.trigger_conditions?.min_records || 0);

  // Action config
  const [actionOem, setActionOem] = useState(rule?.action_config?.oem || '__any__');
  const [samplingStrategy, setSamplingStrategy] = useState<'all' | 'random' | 'first' | 'last'>(
    rule?.action_config?.sampling_strategy || 'all'
  );
  const [samplingCount, setSamplingCount] = useState(rule?.action_config?.sampling_count || 100);

  // Validation state
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Data fetching
  const { data: tenants, isLoading: tenantsLoading } = useTenants();

  // Mutations
  const createMutation = useCreateAutomationRule();
  const updateMutation = useUpdateAutomationRule();
  const isPending = createMutation.isPending || updateMutation.isPending;

  // Reset form when rule changes
  useEffect(() => {
    if (rule) {
      setName(rule.name);
      setDescription(rule.description || '');
      setIsGlobal(!rule.tenant_id);
      setTenantId(rule.tenant_id || '');
      setPriority(rule.priority);
      setIsEnabled(rule.is_enabled);
      setTriggerTaskName(rule.trigger_conditions?.task_name || '');
      setTriggerOem(rule.trigger_conditions?.oem || '__any__');
      setMinRecords(rule.trigger_conditions?.min_records || 0);
      setActionOem(rule.action_config?.oem || '__any__');
      setSamplingStrategy(rule.action_config?.sampling_strategy || 'all');
      setSamplingCount(rule.action_config?.sampling_count || 100);
    } else {
      setName('');
      setDescription('');
      setIsGlobal(true);
      setTenantId(defaultTenantId || '');
      setPriority(100);
      setIsEnabled(true);
      setTriggerTaskName('');
      setTriggerOem('__any__');
      setMinRecords(0);
      setActionOem('__any__');
      setSamplingStrategy('all');
      setSamplingCount(100);
    }
    setErrors({});
  }, [rule, defaultTenantId, open]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!isGlobal && !tenantId) {
      newErrors.tenantId = 'Tenant is required for tenant-specific rules';
    }

    if (priority < 1 || priority > 1000) {
      newErrors.priority = 'Priority must be between 1 and 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle submit
  const handleSubmit = () => {
    if (!validate()) return;

    // Close dialog immediately for instant feedback
    onOpenChange(false);

    // Show success toast immediately
    toast({
      title: isEditing ? 'Rule Updated' : 'Rule Created',
      description: isEditing ? 'Your rule has been updated' : 'Your new rule has been created'
    });

    // Build trigger conditions - convert '__any__' sentinel back to undefined
    const actualTriggerOem = triggerOem === '__any__' ? undefined : triggerOem;
    const triggerConditions: TriggerConditions = {
      source: 'data_collection',
      status: 'completed'
    };
    if (triggerTaskName) triggerConditions.task_name = triggerTaskName;
    if (actualTriggerOem) triggerConditions.oem = actualTriggerOem;
    if (minRecords > 0) triggerConditions.min_records = minRecords;

    // Build action config - convert '__any__' sentinel back to undefined
    const actualActionOem = actionOem === '__any__' ? undefined : actionOem;
    const actionConfig: ActionConfig = {
      entry_point: 'step1_execution',
      sampling_strategy: samplingStrategy
    };
    if (actualActionOem) actionConfig.oem = actualActionOem;
    if (samplingStrategy !== 'all' && samplingCount > 0) {
      actionConfig.sampling_count = samplingCount;
    }

    if (isEditing && rule) {
      const updateData: RuleUpdateRequest = {
        name,
        description: description || undefined,
        trigger_conditions: triggerConditions,
        action_config: actionConfig,
        is_enabled: isEnabled,
        priority
      };

      updateMutation.mutate(
        { id: rule.id, data: updateData },
        {
          onError: error => {
            toast({
              title: 'Failed to update rule',
              description: error instanceof Error ? error.message : 'Unknown error',
              variant: 'destructive'
            });
          }
        }
      );
    } else {
      const createData: RuleCreateRequest = {
        name,
        description: description || undefined,
        tenant_id: isGlobal ? null : tenantId,
        trigger_conditions: triggerConditions,
        action_config: actionConfig,
        is_enabled: isEnabled,
        priority
      };

      createMutation.mutate(createData, {
        onError: error => {
          toast({
            title: 'Failed to create rule',
            description: error instanceof Error ? error.message : 'Unknown error',
            variant: 'destructive'
          });
        }
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Zap className='h-5 w-5' />
            {isEditing ? 'Edit Automation Rule' : 'Create Automation Rule'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modify the automation rule configuration'
              : 'Set up a rule to automatically trigger VIN processing after data collection'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Name */}
          <div className='space-y-2'>
            <Label htmlFor='name'>Name *</Label>
            <Input
              id='name'
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder='e.g., Ford Tampa Auto-VIN'
              className={cn(errors.name && 'border-destructive')}
            />
            {errors.name && <p className='text-sm text-destructive'>{errors.name}</p>}
          </div>

          {/* Description */}
          <div className='space-y-2'>
            <Label htmlFor='description'>Description</Label>
            <Textarea
              id='description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder='Describe what this rule does...'
              rows={2}
            />
          </div>

          {/* Scope: Global vs Tenant-specific */}
          <div className='space-y-4'>
            <Label>Rule Scope</Label>
            <div className='flex gap-4'>
              <Button
                type='button'
                variant={isGlobal ? 'default' : 'outline'}
                className='flex-1'
                onClick={() => setIsGlobal(true)}
              >
                <Globe className='h-4 w-4 mr-2' />
                Global (All Tenants)
              </Button>
              <Button
                type='button'
                variant={!isGlobal ? 'default' : 'outline'}
                className='flex-1'
                onClick={() => setIsGlobal(false)}
              >
                <Building2 className='h-4 w-4 mr-2' />
                Tenant-Specific
              </Button>
            </div>

            {!isGlobal && (
              <div className='space-y-2'>
                <Label htmlFor='tenant'>Tenant *</Label>
                <Select value={tenantId} onValueChange={setTenantId}>
                  <SelectTrigger className={cn(errors.tenantId && 'border-destructive')}>
                    <SelectValue placeholder='Select a tenant...' />
                  </SelectTrigger>
                  <SelectContent>
                    {tenantsLoading ? (
                      <SelectItem value='__loading__' disabled>
                        Loading...
                      </SelectItem>
                    ) : (
                      tenants?.map(tenant => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.tenantId && <p className='text-sm text-destructive'>{errors.tenantId}</p>}
              </div>
            )}
          </div>

          {/* Trigger Conditions */}
          <div className='space-y-4 rounded-lg border p-4'>
            <h4 className='font-medium'>Trigger Conditions</h4>
            <p className='text-sm text-muted-foreground'>
              Define when this rule should be triggered
            </p>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label>Task Name (Optional)</Label>
                <Input
                  value={triggerTaskName}
                  onChange={e => setTriggerTaskName(e.target.value)}
                  placeholder='Match specific task...'
                />
                <p className='text-xs text-muted-foreground'>Leave empty to match all tasks</p>
              </div>

              <div className='space-y-2'>
                <Label>OEM Filter (Optional)</Label>
                <Select value={triggerOem} onValueChange={setTriggerOem}>
                  <SelectTrigger>
                    <SelectValue placeholder='Any OEM' />
                  </SelectTrigger>
                  <SelectContent>
                    {OEM_OPTIONS.map(oem => (
                      <SelectItem key={oem.value} value={oem.value}>
                        {oem.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className='space-y-2'>
              <Label>Minimum Records</Label>
              <Input
                type='number'
                value={minRecords}
                onChange={e => setMinRecords(parseInt(e.target.value) || 0)}
                min={0}
                placeholder='0'
              />
              <p className='text-xs text-muted-foreground'>
                Only trigger if data collection returns at least this many records (0 = no minimum)
              </p>
            </div>
          </div>

          {/* Action Configuration */}
          <div className='space-y-4 rounded-lg border p-4'>
            <h4 className='font-medium'>VIN Processing Action</h4>
            <p className='text-sm text-muted-foreground'>Configure how VINs should be processed</p>

            <div className='grid grid-cols-2 gap-4'>
              <div className='space-y-2'>
                <Label>OEM Override (Optional)</Label>
                <Select value={actionOem} onValueChange={setActionOem}>
                  <SelectTrigger>
                    <SelectValue placeholder='Use source OEM' />
                  </SelectTrigger>
                  <SelectContent>
                    {OEM_OPTIONS.map(oem => (
                      <SelectItem key={oem.value} value={oem.value}>
                        {oem.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className='space-y-2'>
                <Label>Sampling Strategy</Label>
                <Select
                  value={samplingStrategy}
                  onValueChange={(v: 'all' | 'random' | 'first' | 'last') => setSamplingStrategy(v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SAMPLING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {samplingStrategy !== 'all' && (
              <div className='space-y-2'>
                <Label>Sample Count</Label>
                <Input
                  type='number'
                  value={samplingCount}
                  onChange={e => setSamplingCount(parseInt(e.target.value) || 100)}
                  min={1}
                  max={10000}
                />
              </div>
            )}
          </div>

          {/* Priority */}
          <div className='space-y-2'>
            <Label htmlFor='priority'>Priority</Label>
            <Input
              id='priority'
              type='number'
              value={priority}
              onChange={e => setPriority(parseInt(e.target.value) || 100)}
              min={1}
              max={1000}
              className={cn(errors.priority && 'border-destructive')}
            />
            <p className='text-xs text-muted-foreground'>
              Lower numbers = higher priority. Rules are evaluated in order.
            </p>
            {errors.priority && <p className='text-sm text-destructive'>{errors.priority}</p>}
          </div>

          {/* Enabled Toggle */}
          <div className='flex items-center justify-between rounded-lg border p-4'>
            <div className='space-y-0.5'>
              <Label className='font-medium'>Enable Rule</Label>
              <p className='text-sm text-muted-foreground'>
                {isEnabled
                  ? 'Rule is active and will trigger automatically'
                  : 'Rule is paused and will not trigger'}
              </p>
            </div>
            <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
          </div>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                Saving...
              </>
            ) : isEditing ? (
              'Save Changes'
            ) : (
              'Create Rule'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutomationRuleFormDialog;
